import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Button, Chip, Stack, Accordion, AccordionSummary, AccordionDetails,
  TextField, InputAdornment, Tabs, Tab, Paper, Avatar, useTheme, alpha
} from '@mui/material';
import {
  Phonelink, FlashOn, Layers, ExpandMore, ArrowForward,
  Search, CheckCircle, Security, Speed, Code, RocketLaunch,
  HelpOutline, Build, MonetizationOn, Devices, AutoAwesome
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const faqData = [
  {
    category: "Architecture & Performance",
    question: "Why choose Flutter over native iOS (Swift) and Android (Kotlin) development?",
    answer: "Flutter compiles directly to native machine code (ARM/x86) via Dart's AOT (Ahead-Of-Time) compiler. This yields native 60fps/120fps performance while sharing up to 90% of business logic and UI code across platforms, cutting overall development time and maintenance overhead in half."
  },
  {
    category: "Architecture & Performance",
    question: "Can Flutter achieve genuine 60fps or 120fps native performance?",
    answer: "Yes. Flutter uses Impeller (and Skia on legacy setups) to render directly to the GPU canvas. Because it does not rely on JavaScript bridges or OEM native views (unlike React Native or web views), rendering engine latency is virtually eliminated."
  },
  {
    category: "Architecture & Performance",
    question: "What state management architecture do you implement for scalable Flutter apps?",
    answer: "Depending on project scale and client preference, I utilize BLoC (Business Logic Component) for strict enterprise separation of concerns, Riverpod for compile-safe reactive state, or Provider for lightweight applications. All architectures emphasize testability and clean code."
  },
  {
    category: "Architecture & Performance",
    question: "How do you handle platform-specific hardware features (Camera, Bluetooth, GPS)?",
    answer: "I leverage Flutter's native Platform Channels (MethodChannel/EventChannel) to write custom Swift/Objective-C code for iOS and Kotlin/Java code for Android whenever specialized, low-level hardware or OS integration is required."
  },
  {
    category: "Architecture & Performance",
    question: "Is Flutter suitable for enterprise-grade, high-concurrency mobile applications?",
    answer: "Absolutes like 'Flutter runs everywhere' require engineering discipline. Flutter is used by enterprise leaders like Google Pay, Alibaba, and BMW. When combined with clean micro-architecture, strict typing, and decoupled networking layers, it scales seamlessly to millions of active users."
  },
  {
    category: "Cost & Timeline",
    question: "How does Flutter reduce mobile application development costs?",
    answer: "Single-codebase development reduces engineer headcount requirements, eliminates duplicate QA cycles for iOS and Android, and aligns product feature rollouts. Clients typically experience a 35%–50% reduction in total cost of ownership (TCO) compared to dual native teams."
  },
  {
    category: "Cost & Timeline",
    question: "What is the average timeline to launch a Flutter MVP?",
    answer: "A feature-complete MVP typically ranges from 6 to 10 weeks depending on integration complexity, authentication systems, third-party APIs, and offline sync requirements."
  },
  {
    category: "Cost & Timeline",
    question: "What is the cost difference between building native apps vs. a Flutter app?",
    answer: "Building two distinct native apps requires separate Android and iOS engineering pipelines. Flutter reduces initial development labor by roughly 40% and cuts ongoing post-launch maintenance expenses by up to 50%."
  },
  {
    category: "Cost & Timeline",
    question: "Can we expand a Flutter mobile app to Desktop or Web later without rewriting?",
    answer: "Yes. Flutter natively supports Web, macOS, Windows, and Linux target outputs from the same codebase. Responsive layouts and platform adaptation layers ensure smooth multi-screen experiences."
  },
  {
    category: "Cost & Timeline",
    question: "Do you handle Apple App Store and Google Play Store deployment?",
    answer: "Yes. End-to-end service includes bundle signing, test build distribution via TestFlight and Firebase App Distribution, store metadata optimization, and navigating App Store review guidelines."
  },
  {
    category: "Security & Testing",
    question: "How is application security and data encryption handled in Flutter?",
    answer: "Security standards include implementing Flutter Secure Storage (Keychain/Keystore encryption), SSL Pinning to prevent Man-in-the-Middle (MitM) attacks, code obfuscation during release builds, and biometrics integration."
  },
  {
    category: "Security & Testing",
    question: "What automated testing procedures do you implement?",
    answer: "A comprehensive strategy includes Unit Tests for business logic (BLoC/Riverpod), Widget Tests for UI component verification, and automated Integration Tests to validate critical user flows before release."
  },
  {
    category: "Security & Testing",
    question: "How do you manage offline data storage and background synchronization?",
    answer: "I use lightweight embedded databases like Hive or Isar for synchronous fast key-value storage, or SQLite (Moor/Drift) for complex relational local data, backed by background sync workers."
  },
  {
    category: "Security & Testing",
    question: "How do you optimize Flutter APK and IPA binary file sizes?",
    answer: "Through asset compression, tree-shaking unused dynamic icons, deferred loading (dynamic imports), and building platform-specific split binaries (e.g., `--split-per-abi`), release bundles stay lean and fast to download."
  },
  {
    category: "Security & Testing",
    question: "What CI/CD tools do you use for Flutter deployment pipelines?",
    answer: "Automated pipelines are established using Codemagic, GitHub Actions, or Fastlane for continuous integration, running automated test suites on every pull request, and auto-deploying staging builds."
  },
  {
    category: "Process & Delivery",
    question: "Can an existing native Android or iOS app be migrated to Flutter?",
    answer: "Yes. Flutter can be integrated incrementally into existing native codebases as a module ('Add-to-App'), allowing gradual migration of screens without requiring an immediate full rewrite."
  },
  {
    category: "Process & Delivery",
    question: "Do Flutter applications support custom brand design and pixel-perfect animations?",
    answer: "Flutter renders every pixel manually via its canvas. It is fully decoupled from OS design constraints, giving total control over custom design systems, complex vector animations (Rive/Lottie), and brand consistency."
  },
  {
    category: "Process & Delivery",
    question: "How do you integrate legacy REST, GraphQL, or gRPC backend APIs?",
    answer: "Networking layers are built using Dio or Chopper with interceptors for token refresh, request caching, and automated JSON serialization (json_serializable / freezed)."
  },
  {
    category: "Process & Delivery",
    question: "Who owns the source code and intellectual property (IP)?",
    answer: "You own 100% of the intellectual property, repository rights, and production assets. Code is handed over via private Git repositories upon milestone completions."
  },
  {
    category: "Process & Delivery",
    question: "What post-launch support and maintenance plans are provided?",
    answer: "Post-launch services include SLA-backed maintenance covering OS update compatibility (iOS/Android major releases), crash reporting via Sentry/Crashlytics, and feature iteration."
  },
  {
    category: "Process & Delivery",
    question: "How do we kick off a Flutter project with Vikash Tiwari?",
    answer: "The process begins with an initial technical scoping call to review requirements, wireframes, and API documentation. Following scoping, a structured project roadmap, architecture proposal, and timeline are provided."
  }
];

const FlutterDevelopment = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const siteUrl = "https://vikashtiwaridev.web.app/services/flutter-development";
  const pageTitle = "Enterprise Flutter App Development | Vikash Tiwari";
  const pageDescription = "Build high-performance, single-codebase iOS & Android apps with Flutter & Dart. Reduced TCO, native 120fps UI, and scalable BLoC/Riverpod architecture.";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Flutter Cross-Platform App Development",
    "serviceType": "Mobile Application Development",
    "provider": {
      "@type": "Person",
      "name": "Vikash Tiwari",
      "url": "https://vikashtiwaridev.web.app/"
    },
    "areaServed": "Worldwide",
    "description": pageDescription
  };

  // FAQ State Management
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const categories = ['All', 'Architecture & Performance', 'Cost & Timeline', 'Security & Testing', 'Process & Delivery'];

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeTab === 'All' || item.category === activeTab;
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeTab]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const benefits = [
    {
      title: "Single Codebase, Native Speed",
      desc: "Compile directly to native ARM machine code. Reduce time-to-market by 50% without compromising on 60fps/120fps graphic performance.",
      icon: <Speed sx={{ fontSize: 32, color: '#00E5FF' }} />
    },
    {
      title: "Custom GPU Animation Engine",
      desc: "Pixel-perfect material and custom design systems. Direct GPU canvas rendering guarantees visual parity across all mobile devices.",
      icon: <AutoAwesome sx={{ fontSize: 32, color: '#7C4DFF' }} />
    },
    {
      title: "Enterprise Architecture",
      desc: "Decoupled state management utilizing BLoC or Riverpod. Built for high test coverage, clean code isolation, and rapid developer scaling.",
      icon: <Layers sx={{ fontSize: 32, color: '#00E5FF' }} />
    },
    {
      title: "Native Hardware Integration",
      desc: "Direct integration with native iOS (Swift) and Android (Kotlin) APIs via custom MethodChannels for complex hardware capabilities.",
      icon: <Phonelink sx={{ fontSize: 32, color: '#7C4DFF' }} />
    }
  ];

  const metrics = [
    { value: "50%", label: "Faster Delivery" },
    { value: "60-120", label: "Target FPS UI" },
    { value: "90%+", label: "Shared Codebase" },
    { value: "100%", label: "IP Ownership" },
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="icon" type="image/x-icon" href="https://vikashtiwaridev.web.app/VikashTiwariIcon.png" />
        <meta name="keywords" content="Flutter Developer, Dart Developer, Cross Platform App Development, Hire Flutter Engineer, iOS and Android Apps, BLoC Architecture" />
        <link rel="canonical" href={siteUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Box component="main" sx={{ bgcolor: '#0A0E17', color: '#F1F5F9', minHeight: '100vh', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <Box
          sx={{
            position: 'relative',
            pt: { xs: 10, md: 16 },
            pb: { xs: 10, md: 14 },
            background: 'radial-gradient(circle at 50% 10%, rgba(124, 77, 255, 0.15) 0%, rgba(0, 229, 255, 0.05) 45%, rgba(10, 14, 23, 1) 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          {/* Subtle Ambient Glow */}
          <Box
            sx={{
              position: 'absolute', top: '15%', left: '50%', transform: 'translateX(-50%)',
              width: { xs: '300px', md: '600px' }, height: { xs: '300px', md: '600px' },
              background: 'radial-gradient(circle, rgba(0, 229, 255, 0.12) 0%, rgba(124, 77, 255, 0) 70%)',
              filter: 'blur(80px)', pointerEvents: 'none'
            }}
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ maxWidth: '880px', mx: 'auto', textAlign: 'center' }}>

              <Chip
                icon={<AutoAwesome sx={{ fontSize: '18px !important', color: '#00E5FF' }} />}
                label="High-Performance Cross-Platform Engineering"
                sx={{
                  mb: 3,
                  py: 2,
                  px: 1,
                  bgcolor: alpha('#00E5FF', 0.08),
                  color: '#00E5FF',
                  border: '1px solid rgba(0, 229, 255, 0.3)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  backdropFilter: 'blur(10px)'
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.25rem' },
                  fontWeight: 900,
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                Build Native-Grade iOS & Android Apps with{' '}
                <Box
                  component="span"
                  sx={{
                    background: 'linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  Flutter
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: '#94A3B8',
                  fontSize: { xs: '1.1rem', md: '1.35rem' },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  mb: 5,
                  maxWidth: '760px',
                  mx: 'auto'
                }}
              >
                Eliminate double codebase overhead. Architecting high-concurrency, pixel-perfect,
                and secure mobile applications compiled directly to native machine code.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="center"
                sx={{ mb: 8 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/contact')}
                  sx={{
                    py: 1.8,
                    px: 4,
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #7C4DFF 0%, #00E5FF 100%)',
                    boxShadow: '0px 8px 24px rgba(124, 77, 255, 0.35)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #6B38FF 0%, #00D2EE 100%)',
                      boxShadow: '0px 12px 32px rgba(124, 77, 255, 0.5)',
                    }
                  }}
                >
                  Schedule Scoping Call
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    const el = document.getElementById('faq-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{
                    py: 1.8,
                    px: 4,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#F1F5F9',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: '#00E5FF',
                      bgcolor: alpha('#00E5FF', 0.05)
                    }
                  }}
                >
                  Explore FAQs (20+)
                </Button>
              </Stack>

              {/* Stats Bar */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {metrics.map((m, idx) => (
                  <Grid item xs={6} sm={3} key={idx}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '16px',
                        bgcolor: 'rgba(15, 23, 42, 0.6)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)'
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          background: 'linear-gradient(90deg, #00E5FF, #7C4DFF)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5
                        }}
                      >
                        {m.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                        {m.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

            </Box>
          </Container>
        </Box>


        {/* ================= CORE ARCHITECTURE BENEFITS (2x2 Grid) ================= */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 15 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 800,
                mb: 2,
                letterSpacing: '-0.01em'
              }}
            >
              Why Enterprise Projects Build with Flutter
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '650px', mx: 'auto', fontSize: '1.1rem' }}>
              Delivering high-performance architecture optimized for execution speed, maintainability, and predictable scalability.
            </Typography>
          </Box>

          {/* Fixed 2x2 CSS Grid Layout */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              rowGap: { xs: 3, md: 8 },    // Increased vertical separation for breathing room
              columnGap: { xs: 3, md: 4 }  // Maintained horizontal grid alignment
            }}
          >
            {benefits.slice(0, 4).map((item, i) => (
              <Card
                key={i}
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: '20px',
                  bgcolor: 'rgba(15, 23, 42, 0.5)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  p: 3, // Single source of padding on parent Card container
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(0, 229, 255, 0.4)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
                    bgcolor: 'rgba(15, 23, 42, 0.8)'
                  }
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}> {/* Reset internal MUI CardContent overrides */}
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '14px',
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 3
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontSize: '1.35rem', fontWeight: 700, mb: 1.5, color: '#F8FAFC' }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8', lineHeight: 1.7, fontSize: '0.975rem' }}>
                    {item.desc}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>

        {/* ================= TECH STACK & ECOSYSTEM ================= */}
        <Box
          sx={{
            py: { xs: 8, md: 10 },
            bgcolor: 'rgba(15, 23, 42, 0.7)',
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(10, 14, 23, 0.8) 100%)'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, mb: 2 }}>
                Technical Capabilities & Tooling
              </Typography>
              <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                Production-proven libraries, databases, state layers, and CI/CD automation pipelines.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ gap: 1.5 }}>
              {[
                "Dart 3.x", "Flutter SDK", "BLoC Pattern", "Riverpod", "Provider",
                "Firebase Suite", "REST APIs", "GraphQL", "Hive / Isar DB", "SQLite / Drift",
                "MethodChannels", "Codemagic CI/CD", "GitHub Actions", "Fastlane", "Rive Animations",
                "Sentry Crashlytics", "Clean Architecture", "Unit & Widget Testing"
              ].map((tech) => (
                <Chip
                  key={tech}
                  label={tech}
                  sx={{
                    py: 2.5, px: 2,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    color: '#E2E8F0',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha('#00E5FF', 0.1),
                      borderColor: '#00E5FF',
                      color: '#00E5FF'
                    }
                  }}
                />
              ))}
            </Stack>
          </Container>
        </Box>


        {/* ================= DYNAMIC FAQ SECTION (20+ Qs with Search & Filters) ================= */}
        <Container maxWidth="lg" id="faq-section" sx={{ py: { xs: 8, md: 12 } }}>

          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip
              icon={<HelpOutline sx={{ fontSize: '18px !important', color: '#7C4DFF' }} />}
              label="Comprehensive Scoping & Technical FAQs"
              sx={{ mb: 2, bgcolor: alpha('#7C4DFF', 0.1), color: '#7C4DFF', fontWeight: 600 }}
            />
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2 }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto' }}>
              Detailed answers covering architecture, development timelines, cost structures, and security standards.
            </Typography>
          </Box>

          {/* FAQ Controls: Search and Filter Tabs */}
          <Box sx={{ maxWidth: '800px', mx: 'auto', mb: 5 }}>
            <TextField
              fullWidth
              placeholder="Search technical questions (e.g., security, BLoC, cost, native)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#00E5FF' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  bgcolor: 'rgba(15, 23, 42, 0.8)',
                  color: '#F8FAFC',
                  fieldset: { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#00E5FF' },
                  '&.Mui-focused fieldset': { borderColor: '#7C4DFF' }
                }
              }}
            />

            {/* Category Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, newVal) => setActiveTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#00E5FF', height: '3px', borderRadius: '3px' },
                '& .MuiTab-root': {
                  color: '#94A3B8',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  '&.Mui-selected': { color: '#00E5FF' }
                }
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Box>

          {/* Accordions List */}
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const panelId = `panel-${index}`;
                return (
                  <Accordion
                    key={index}
                    expanded={expandedFaq === panelId}
                    onChange={handleAccordionChange(panelId)}
                    sx={{
                      bgcolor: 'rgba(15, 23, 42, 0.6)',
                      color: '#F1F5F9',
                      mb: 2,
                      borderRadius: '14px !important',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(10px)',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        borderColor: 'rgba(0, 229, 255, 0.3)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#00E5FF' }} />}
                      sx={{
                        p: 2.5,
                        '& .MuiAccordionSummary-content': { my: 0 }
                      }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600, pr: 2 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, pt: 0, color: '#94A3B8', lineHeight: 1.7 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.975rem' }}>
                        {faq.answer}
                      </Typography>
                      <Chip
                        label={faq.category}
                        size="small"
                        sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748B', fontSize: '0.75rem' }}
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#64748B' }}>
                <Typography variant="h6">No matching questions found.</Typography>
                <Typography variant="body2">Try adjusting your search query or switching categories.</Typography>
              </Box>
            )}
          </Box>

        </Container>


        {/* ================= CALL TO ACTION BANNER ================= */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 5, md: 8 },
              borderRadius: '28px',
              background: 'linear-gradient(135deg, rgba(124, 77, 255, 0.2) 0%, rgba(0, 229, 255, 0.1) 100%)',
              border: '1px solid rgba(0, 229, 255, 0.2)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, mb: 2 }}>
              Ready to Build Your Flutter Mobile App?
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
              Convert your product vision into a production-grade, dual-platform mobile solution.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/contact')}
              sx={{
                py: 1.8,
                px: 5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: '12px',
                textTransform: 'none',
                background: '#F8FAFC',
                color: '#0A0E17',
                '&:hover': {
                  background: '#FFFFFF',
                  boxShadow: '0px 8px 24px rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Get Started
            </Button>
          </Paper>
        </Container>

      </Box>
    </>
  );
};

export default FlutterDevelopment;

// import React from 'react';
// import { Helmet } from 'react-helmet-async';
// import {
//   Box, Container, Typography, Grid, Card, CardContent,
//   Button, Chip, Stack, Accordion, AccordionSummary, AccordionDetails
// } from '@mui/material';
// import {
//   Phonelink, FlashOn, Layers, ExpandMore, ArrowForward
// } from '@mui/icons-material';
// import { useNavigate } from 'react-router-dom';

// const FlutterDevelopment = () => {
//   const navigate = useNavigate();
//   const siteUrl = "https://vikashtiwaridev.web.app/services/flutter-development";
//   const pageTitle = "Cross-Platform Flutter App Development | Vikash Tiwari";
//   const pageDescription = "Build beautiful, high-speed iOS and Android applications from a single Dart codebase with Flutter. Reduced development cost and faster time-to-market.";

//   const structuredData = {
//     "@context": "https://schema.org",
//     "@type": "Service",
//     "name": "Flutter Cross-Platform App Development",
//     "serviceType": "Mobile Application Development",
//     "provider": {
//       "@type": "Person",
//       "name": "Vikash Tiwari",
//       "url": "https://vikashtiwaridev.web.app/"
//     },
//     "areaServed": "Worldwide",
//     "description": pageDescription
//   };

//   const benefits = [
//     { title: "Single Codebase for iOS & Android", desc: "Halve your development time and maintenance cost while delivering native performance across platforms." },
//     { title: "Custom UI & Animation Engine", desc: "Pixel-perfect Material Design and Cupertino widgets tailored to your brand identity." },
//     { title: "State Management Expertise", desc: "Scalable architecture utilizing Provider, Bloc, or Riverpod based on project complexity." },
//     { title: "Native Platform Channels", desc: "Seamless integration with native Android (Kotlin) and iOS (Swift) APIs when hardware access is required." }
//   ];

//   return (
//     <>
//       <Helmet>
//         <title>{pageTitle}</title>
//         <meta name="description" content={pageDescription} />
//         <meta name="keywords" content="Flutter Developer, Dart Developer, Cross Platform App Development, Hire Flutter Engineer, iOS and Android Apps" />
//         <link rel="canonical" href={siteUrl} />
//         <meta property="og:type" content="website" />
//         <meta property="og:title" content={pageTitle} />
//         <meta property="og:description" content={pageDescription} />
//         <meta property="og:url" content={siteUrl} />
//         <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
//       </Helmet>

//       <Box component="main" sx={{ py: 8 }}>
//         <Container maxWidth="lg">
//           <Box sx={{ mb: 8, textAlign: 'center' }}>
//             <Chip icon={<Phonelink />} label="Cross-Platform Solutions" color="secondary" sx={{ mb: 2 }} />
//             <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 800, mb: 2 }}>
//               Flutter Mobile Development
//             </Typography>
//             <Typography variant="h5" color="text.secondary" sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}>
//               Deliver multi-platform mobile applications with smooth 60fps/120fps UI rendering and shared business logic.
//             </Typography>
//             <Button variant="contained" color="secondary" size="large" endIcon={<ArrowForward />} onClick={() => navigate('/contact')}>
//               Start Flutter Project
//             </Button>
//           </Box>

//           <Typography variant="h2" sx={{ fontSize: '2rem', fontWeight: 700, mb: 4, textAlign: 'center' }}>
//             Why Choose Flutter?
//           </Typography>
//           <Grid container spacing={4} sx={{ mb: 8 }}>
//             {benefits.map((item, i) => (
//               <Grid item xs={12} sm={6} key={i}>
//                 <Card variant="outlined" sx={{ height: '100%', borderRadius: 3, p: 1 }}>
//                   <CardContent>
//                     <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 700, mb: 1 }}>
//                       {item.title}
//                     </Typography>
//                     <Typography color="text.secondary">{item.desc}</Typography>
//                   </CardContent>
//                 </Card>
//               </Grid>
//             ))}
//           </Grid>

//           <Box sx={{ p: 4, bgcolor: 'action.hover', borderRadius: 4, mb: 8, textAlign: 'center' }}>
//             <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 3 }}>
//               Flutter Ecosystem Skills
//             </Typography>
//             <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ gap: 1 }}>
//               {["Dart", "Flutter SDK", "Bloc / Riverpod", "Firebase", "REST & GraphQL", "Hive / Isar DB", "Fl_Chart", "MethodChannels", "CI/CD Codemagic"].map((tech) => (
//                 <Chip key={tech} label={tech} variant="outlined" color="secondary" />
//               ))}
//             </Stack>
//           </Box>
//         </Container>
//       </Box>
//     </>
//   );
// };

// export default FlutterDevelopment;